import { useTranslation } from 'react-i18next';
import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Calendar as CalendarComponent } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  SkipBack, 
  SkipForward, 
  CalendarIcon, 
  Copy, 
  Download 
} from 'lucide-react';
import { format } from 'date-fns';
import { formatDateDDMMYYYY, getWeekNumber } from '../utils/helpers';

const PlanNavigation = ({
  weekNumber,
  viewStartDate,
  viewEndDate,
  planStartDate,
  planEndDate,
  weekOptions,
  currentWeekValue,
  datePickerOpen,
  setDatePickerOpen,
  onNavigate,
  onGoToPlanStart,
  onGoToPlanEnd,
  onGoToCurrentWeek,
  onWeekDropdownChange,
  onDateSelect,
  onQuickCopyWeek,
  onCopyMultiWeeks,
  onExport
}) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={onGoToPlanStart} title={t('auto.ctr.planStart')} data-testid="btn-gotoplanstart">
              <SkipBack className="h-4 w-4" />
            </Button>
            
            <Button variant="outline" size="sm" onClick={() => onNavigate(-1)} title={t('auto.ctr.previousWeek')}>
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('auto.ctr.prev')}
            </Button>
            
            {/* Week Dropdown */}
            <Select value={currentWeekValue} onValueChange={onWeekDropdownChange}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder={t('auto.ctr.selectAWeek')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {weekOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Picker */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  {t('auto.ctr.pickDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  mode="single"
                  selected={viewStartDate}
                  onSelect={onDateSelect}
                  initialFocus
                  disabled={(date) => date < planStartDate || date > planEndDate}
                />
              </PopoverContent>
            </Popover>

            <Button variant="outline" size="sm" onClick={() => onNavigate(1)} title={t('auto.ctr.nextWeek')} data-testid="btn-next">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            
            <Button variant="outline" size="sm" onClick={onGoToPlanEnd} title={t('auto.ctr.planEnd')} data-testid="btn-gotoplanend">
              <SkipForward className="h-4 w-4" />
            </Button>
            
            <Button variant="outline" size="sm" onClick={onGoToCurrentWeek} title={t('auto.ctr.currentWeek')} data-testid="btn-gotocurrentweek">
              <Home className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Copy Week Actions */}
            <div className="border-l pl-2 ml-1 flex items-center gap-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onQuickCopyWeek}
                className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                title={t('auto.ctr.quickCopyCurrentWeekToNext')}
                data-testid="quick-copy-week-btn"
              >
                <Copy className="h-4 w-4 mr-1" /> {t('auto.ctr.copyToNext')}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onCopyMultiWeeks}
                className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                title={t('auto.ctr.copyToMultipleWeeks')}
               data-testid="btn-copymultiweeks">
                <Copy className="h-4 w-4 mr-1" /> {t('auto.ctr.copyTo')}
              </Button>
            </div>
            
            {/* Export */}
            <div className="border-l pl-2 ml-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onExport}
                className="bg-green-50 hover:bg-green-100 border-green-200 text-green-700"
                title={t('auto.ctr.exportToExcel')}
                data-testid="export-plan-btn"
              >
                <Download className="h-4 w-4 mr-1" /> {t('auto.ctr.export')}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Week info display */}
        <div className="mt-2 text-sm text-gray-600 text-center">
          Week {weekNumber} • {formatDateDDMMYYYY(viewStartDate)} - {formatDateDDMMYYYY(viewEndDate)}
        </div>
      </CardContent>
    </Card>
  );
};

export default PlanNavigation;
